import {ReactElement} from "react";
import {ConstraintType, EnumerationConstraintDataDto} from "../../../../../../common/apis/datatype/DataTypeConstraintDto";
import {ElementDto} from "../../../../../../common/apis/element/ElementDto";
import {AttributeDefinitionDto} from "../../../../../../common/apis/stereotype/AttributeDefinitionDto";
import SingleSelectField from "../../../../../../components/fields/select/SingleSelectField";
import TextField from "../../../../../../components/fields/textfield/TextField";
import {t} from "../../../../../../store/localization/TranslMessasge";
import elementService from "../../../elementdetail/service/ElementService";
import {StereotypesTranslationKey} from "../../StereotypesTranslationKey";
import {SelectField as SelectFieldComponent} from "../model/SelectField";
import {UiComponent} from "../model/UiComponent";
import {
    UiComponentDataContext,
    UiComponentRendererPlugin,
    UiComponentRenderingContext,
    UiComponentRenderingProps,
    UiComponentStateContext,
    UiComponentValidationContext
} from "../UiComponentRendererPlugin";
import ErrorFieldPlaceholder from "./ErrorFieldPlaceholder";
import {PropertyValuePersister} from "./PropertyValuePersister";
import {ValuePersister} from "./ValuePersister";

export class SelectFieldPlugin implements UiComponentRendererPlugin {

    private readonly valuePersister: ValuePersister = new PropertyValuePersister(elementService);

    getSupportedComponentTypes(): string[] {
        return ["SelectField"];
    }

    getInitialValues(component: UiComponent, dataContext: UiComponentDataContext, renderingContext: UiComponentRenderingContext): Promise<Record<string, any>> {
        const attributeDefinitionId = component.attributeDefinitionId;
        const value = this.getPropertyValue(dataContext.element, dataContext.attributeDefinitions[component.attributeDefinitionId]);
        if (value) {
            return Promise.resolve({
                [attributeDefinitionId]: value
            });
        } else {
            return Promise.resolve({});
        }
    }

    private getPropertyValue(element: ElementDto, attributeDefinition?: AttributeDefinitionDto): string | undefined {
        const property = element.properties?.find(prop => prop.definition.name === attributeDefinition?.name);
        if (property) {
            return property.value;
        } else {
            return undefined;
        }
    }

    validate(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext, validationContext: UiComponentValidationContext): Record<string, string> {
        const selectField = component as SelectFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[selectField.attributeDefinitionId];
        if (!attributeDefinition) {
            return {};
        }

        const value = stateContext.values[selectField.attributeDefinitionId];
        return validationContext.valueValidator.validateValue(value, attributeDefinition, t);
    }

    renderComponent({component, dataContext, stateContext, renderingContext}: UiComponentRenderingProps): ReactElement {
        const selectField = component as SelectFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[selectField.attributeDefinitionId];
        if (!attributeDefinition) {
            return (
                    <ErrorFieldPlaceholder key={selectField.id} error={t(StereotypesTranslationKey.UNKNOWN_ATTRIBUTE)} />
            );
        }

        const value = stateContext.values[selectField.attributeDefinitionId];
        const error = stateContext.errors[selectField.attributeDefinitionId];
        const readOnly = selectField.readOnly || !stateContext.editMode;

        function onChange(value: any) {
            if (!readOnly) {
                stateContext.onValuesChange({[selectField.attributeDefinitionId]: value});
            }
        }

        const enumerationConstraint = attributeDefinition.dataType.constraints?.find(constraint => constraint.constraintData.type === ConstraintType.ENUMERATION);
        let options: string[] = [];
        if (enumerationConstraint) {
            const constraintData = enumerationConstraint.constraintData as EnumerationConstraintDataDto;
            options = constraintData.values;
        }

        if (readOnly) {
            return (
                    <TextField
                            key={selectField.id}
                            label={attributeDefinition.name}
                            value={value ?? ""}
                            required={attributeDefinition.required}
                            InputProps={{
                                readOnly: true,
                            }}
                            fullWidth
                    />
            );
        }

        return (
                <SingleSelectField
                        key={selectField.id}
                        id={selectField.id}
                        label={attributeDefinition.name}
                        value={value}
                        onChange={onChange}
                        options={options}
                        getOptionLabel={option => option}
                        errorMessage={error ?? undefined}
                        required={attributeDefinition.required}
                        readOnly={readOnly}
                />
        );
    }

    save(component: UiComponent, dataContext: UiComponentDataContext, stateContext: UiComponentStateContext): Promise<void>[] {
        const selectField = component as SelectFieldComponent;

        const attributeDefinition: AttributeDefinitionDto = dataContext.attributeDefinitions[selectField.attributeDefinitionId];
        if (!attributeDefinition) {
            return [];
        }

        const value = stateContext.values[selectField.attributeDefinitionId];
        return [this.valuePersister.updateValue(dataContext.element, attributeDefinition, value)];
    }

}